/* eslint-disable */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectLoading, addAlert } from "src/modules/notification/redux";
import {
  getNodesWithWhiteLabel,
  getCompanyUsers,
  putBranchWhiteLabel,
  removeBranchWhiteLabel,
  postCreditTransfer,
  postReimbursedCredit,
  clearErrors,
  getBundleOptions,
  getCompanyFeature,
  setCompanyBundle,
  clearCompanyFeature,
} from "src/modules/companies/redux";
import { getModuleListing, getStructure } from "src/modules/bundles/redux/bundles.action";
import resolveBundleId, {
  isMatchedBundleError,
} from "src/modules/bundles/utils/resolve-bundle-id";
import { getAvailablePlans } from "src/modules/approvals/redux/action";
import {
  getCompanyPlanInfo,
  updateCompanyPlan,
} from "src/modules/companies/redux/action/companies.action";
import {
  addBranchWhiteLabelCount,
  minusBranchWhiteLabelCount,
} from "../../../redux/slice";
import { unwrapResult } from "@reduxjs/toolkit";
import CompanyDetailsDialogComponent from "./company-details-dialog.component";
import companiesApi from "../../../../../configs/apis/companies";
import updateCompanySalesEmail from "src/configs/apis/companies/updateComapnySalesEmail.api";
import AccessRights from "../../../../../configs/constants/accessRights";

export default function CompanyDetailsDialogContainer({
  industries,
  selectedCompany,
  onCompanyDetailsDialogCloseClick,
  onCompanyWhiteLabelDialogSubmitClick,
  onRemoveCompanyWhiteLabelClick,
  onCompanyUsersViewClick,
  onCompanyFeatureSaved,
}) {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(0); // 0=Company Information, 1=White Label, 2=Manage Branch, 3=Users

  const companyBranch = useSelector(
    (state) => state.companies.nodesWithWhiteLabel
  );
  const companyManageBranchPage = useSelector(
    (state) => state.companies.companyManageBranchPage
  );
  const totalCompanyManageBranch = useSelector(
    (state) => state.companies.totalCompanyManageBranch
  );
  const [selectedBranchWhiteLabel, setSelectedBranchWhiteLabel] =
    useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentBranchPage, setCurrentBranchPage] = useState(0);
  const [confirmationDialogModal, setConfirmationDialogModal] = useState(false);

  const isRemovingBranch = useSelector((state) =>
    selectLoading(state, removeBranchWhiteLabel.typePrefix)
  );
  const isManageBranchLoading = useSelector((state) =>
    selectLoading(state, getNodesWithWhiteLabel.typePrefix)
  );

  const companyUsers = useSelector((state) => state.companyUsers.users);
  const totalUsers = useSelector((state) => state.companyUsers.totalUsers);
  const currentUsersPage = useSelector((state) => state.companyUsers.usersPage);
  const isCompanyUsersLoading = useSelector(
    (state) => state.companyUsers.isLoading
  );
  const completeDialogErrors = useSelector(
    (state) => state.companyUsers.errors
  );

  const [usersRowsPerPage, setUsersRowsPerPage] = useState(10);
  const [query, setQuery] = useState({
    searchColumn: { name: "Username", id: "username" },
    searchValue: "",
    status: { name: "All Status", id: "" },
  });
  const [selectedCompanyUser, setSelectedCompanyUser] = useState(null);
  const [openCreditTransferConfirmation, setOpenCreditTransferConfirmation] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openCompleteDialog, setOpenCompleteDialog] = useState(false);

  // Expired Management Tab State
  const [editMode, setEditMode] = useState(false);
  const [monthsInput, setMonthsInput] = useState("");
  const [expiredPlan, setExpiredPlan] = useState("");
  const [companyData, setCompanyData] = useState(null);
  const [loadingExpired, setLoadingExpired] = useState(false);
  const [updatingExpired, setUpdatingExpired] = useState(false);

  // Sales Email Tab State
  const [isSubmittingSalesEmail, setIsSubmittingSalesEmail] = useState(false);

  // Swap Plan Tab State
  const planInfo = useSelector((state) => state.companies.planInfo);
  const plans = useSelector((state) => state.approvals.plans);
  const [isLoadingSwapPlan, setIsLoadingSwapPlan] = useState(false);
  const [isUpdatingSwapPlan, setIsUpdatingSwapPlan] = useState(false);

  // Tag Configuration State
  const [companyTags, setCompanyTags] = useState([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [isUpdatingTags, setIsUpdatingTags] = useState(false);

  // Company Features Tab State — the same three selectors the standalone company bundle
  // dialog reads, so both surfaces describe the same company the same way.
  const bundleOptions = useSelector((state) => state.companies.bundleOptions);
  // A bundle only carries module ids; the catalogue turns them into cards.
  const moduleListing = useSelector((state) => state.bundles.moduleListing);
  // The full system-feature tree, used ONLY to draw the read-only effective-access
  // view for a company on no bundle (Decision #19). Static, so it is fetched once
  // when the tab opens; the assigned case never reads it.
  const featureStructure = useSelector((state) => state.bundles.structure);
  const companyFeature = useSelector((state) => state.companies.companyFeature);
  // Why the last read of this company's bundle failed, if it did. The panel needs it to tell
  // "not back yet" from "it failed" — the difference between a spinner and a dead end.
  const companyFeatureError = useSelector(
    (state) => state.companies.companyFeatureError
  );
  const isCompanyFeatureLoading = useSelector((state) =>
    selectLoading(state, getBundleOptions.typePrefix)
  );
  // Whether the two catalogues have ever come back — not whether they have anything in them.
  // The editor needs the difference to tell "still loading" from "there are no bundles yet",
  // which an empty list on its own cannot say.
  const isCompanyFeatureLoaded = useSelector(
    (state) => state.companies.bundleOptionsLoaded && state.bundles.moduleListingLoaded
  );
  // Either catalogue read was rejected. The editor only acts on it while nothing has ever
  // loaded — which is the difference between "wait" and "waiting will not help".
  const hasCompanyFeatureLoadFailed = useSelector(
    (state) => state.companies.bundleOptionsFailed || state.bundles.moduleListingFailed
  );
  const [isUpdatingCompanyFeature, setIsUpdatingCompanyFeature] = useState(false);

  // Access rights for user actions
  const canImpersonate = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.COMPANY_USER_IMPERSONATE)
  );
  const canTransferCredit = useSelector((state) =>
    state.session.accessRights.includes(
      AccessRights.COMPANY_USER_CREDIT_TRANSFER
    )
  );

  const selectedCompanyForUsers =
    currentTab === 4 && selectedCompany
      ? {
          companyId: selectedCompany.company.id,
          companyPlan: selectedCompany.company.plan,
        }
      : null;

  // Load Company Features data when tab changes to Company Features (tab 2)
  //
  // `state.companies.companyFeature` is a single slot shared with the standalone bundle
  // dialog, and this container is never unmounted (the companies page renders it with a null
  // company). Leaving the tab or closing the dialog therefore has to RELEASE the slot,
  // otherwise the panel could be seeded from whichever company was looked at last — and Save
  // would write that company's bundle onto this one.
  useEffect(() => {
    if (selectedCompany && currentTab === 2) {
      const companyId = (selectedCompany.company && selectedCompany.company.id)
        || selectedCompany.id;
      dispatch(getBundleOptions());
      // This surface must show the FULL module set (a company can sit on a bundle that
      // includes a since-retired module). The cache is shared with the bundle-authoring
      // dialogs, which fill it with the active-only subset, so always refetch unfiltered on
      // open rather than trusting whatever an authoring session may have left cached.
      dispatch(getModuleListing());
      // The full feature tree is likewise static and only feeds the read-only
      // no-bundle view (Decision #19); fetch it once.
      if (!featureStructure.length) {
        dispatch(getStructure());
      }
      dispatch(getCompanyFeature({ id: companyId }));
    }
    return () => {
      dispatch(clearCompanyFeature());
    };
  }, [selectedCompany, currentTab, dispatch]);

  // Load Manage Branch data when tab changes to Manage Branch (tab 3)
  useEffect(() => {
    if (currentTab === 3 && selectedCompany) {
      dispatch(
        getNodesWithWhiteLabel({
          id: selectedCompany.company.id,
          page: 0,
          length: rowsPerPage,
        })
      );
    }
  }, [currentTab, selectedCompany, dispatch, rowsPerPage]);

  // Load Users data when tab changes to Users (tab 4)
  useEffect(() => {
    if (currentTab === 4 && selectedCompany) {
      dispatch(
        getCompanyUsers({
          companyId: selectedCompany.company.id,
          companyPlan: selectedCompany.company.plan,
          page: 0,
          length: usersRowsPerPage,
          filter: query,
        })
      );
    }
  }, [currentTab, selectedCompany, dispatch, usersRowsPerPage, query]);

  // Load Expired Date when tab changes to Expired Management (tab 5)
  useEffect(() => {
    if (selectedCompany && currentTab === 5) {
      setExpiredPlan((selectedCompany.company && selectedCompany.company.plan) || "");
      setMonthsInput("");
      setLoadingExpired(true);
      companiesApi
        .getCompanyExpiredDate(selectedCompany.company.id)
        .then((response) => {
          if (response.success) {
            setCompanyData({
              ...selectedCompany,
              company: {
                ...selectedCompany.company,
                expired_at: response.data.expired_at,
                deactive_at: response.data.deactive_at,
              },
            });
          }
        })
        .finally(() => {
          setLoadingExpired(false);
        });
    }
  }, [selectedCompany, currentTab]);

  // Load Swap Plan data when tab changes to Swap Plan (tab 7)
  useEffect(() => {
    if (selectedCompany && currentTab === 7) {
      setIsLoadingSwapPlan(true);
      const companyId = (selectedCompany.company && selectedCompany.company.id)
        || selectedCompany.id;

      Promise.all([
        dispatch(getAvailablePlans())
          .unwrap()
          .catch((error) => {
            console.error("Error fetching available plans:", error);
            // Don't fail the whole operation if just this API fails
            return null;
          }),
        dispatch(getCompanyPlanInfo(companyId))
          .unwrap()
          .catch((error) => {
            console.error("Error fetching company plan info:", error);
            return null;
          }),
      ]).finally(() => {
        setIsLoadingSwapPlan(false);
      });
    }
  }, [selectedCompany, currentTab, dispatch]);

  // Load Tags when tab changes to Configuration (tab 1)
  useEffect(() => {
    if (selectedCompany && currentTab === 1) {
      const uuid = selectedCompany.company ? selectedCompany.company.id : undefined;
      setIsLoadingTags(true);
      companiesApi
        .getCompanyTags({ uuid })
        .then((response) => {
          if (response.data && Array.isArray(response.data)) {
            setCompanyTags(response.data);
            console.log('test');
          } else {
            setCompanyTags([]);
          }
        })
        .catch((error) => {
          console.error("Error fetching company tags:", error);
          setCompanyTags([]);
        })
        .finally(() => {
          setIsLoadingTags(false);
        });
    }
  }, [selectedCompany, currentTab]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleClose = () => {
    setCurrentTab(0);
    // Reset expired management state
    setEditMode(false);
    setMonthsInput("");
    setCompanyData(null);
    onCompanyDetailsDialogCloseClick();
  };

  const onCompanyManageBranchPageClick = (page) => {
    if (companyManageBranchPage !== page) {
      setCurrentBranchPage(page);
      dispatch(
        getNodesWithWhiteLabel({
          id: selectedCompany.company.id,
          page,
          length: rowsPerPage,
        })
      );
    }
  };

  const handleItemPerPageChange = (value) => {
    setRowsPerPage(value);
  };

  const onBranchWhiteLabelClick = (company) => {
    setSelectedBranchWhiteLabel(company);
  };

  const onBranchWhiteLabelDialogSubmitClick = (values) => {
    dispatch(
      putBranchWhiteLabel({
        id: selectedBranchWhiteLabel.uuid,
        whiteLabel: values.whiteLabel,
      })
    )
      .then(unwrapResult)
      .then(() => {
        dispatch(
          getNodesWithWhiteLabel({
            id: selectedCompany.company.id,
            page: 0,
            length: rowsPerPage,
          })
        );
        if (!selectedBranchWhiteLabel.white_label) {
          dispatch(addBranchWhiteLabelCount(selectedCompany.company.row));
        }
        dispatch(
          addAlert({
            severity: "success",
            message: "Node's white label updated successfully",
          })
        );
      })
      .finally(() => {
        setSelectedBranchWhiteLabel(null);
      });
  };

  const onBranchWhiteLabelDialogCloseClick = () => {
    setSelectedBranchWhiteLabel(null);
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialogModal(false);
  };

  const setConfirmationDialog = () => {
    setConfirmationDialogModal(true);
  };

  const onRemoveBranchWhiteLabelClick = () => {
    setConfirmationDialogModal(false);
    dispatch(
      removeBranchWhiteLabel({
        id: selectedBranchWhiteLabel.uuid,
      })
    )
      .then(unwrapResult)
      .then(() => {
        dispatch(
          getNodesWithWhiteLabel({
            id: selectedCompany.company.id,
            page: 0,
            length: rowsPerPage,
          })
        );
        dispatch(minusBranchWhiteLabelCount(selectedCompany.company.row));
        dispatch(
          addAlert({
            severity: "success",
            message: "Node's white label removed successfully",
          })
        );
      })
      .finally(() => {
        setSelectedBranchWhiteLabel(null);
      });
  };

  const onCompanyUsersPageClick = async (page) => {
    if (currentUsersPage !== page) {
      dispatch(
        getCompanyUsers({
          companyId: selectedCompanyForUsers.companyId,
          companyPlan: selectedCompanyForUsers.companyPlan,
          page,
          length: usersRowsPerPage,
          filter: query,
        })
      );
    }
  };

  const onClearFilter = async () => {
    setQuery({
      searchColumn: { name: "Username", id: "username" },
      searchValue: "",
      status: { name: "All Status", id: "" },
    });
  };

  const handleUsersItemPerPageChange = (value) => {
    setUsersRowsPerPage(value);
  };

  const onSearchSubmit = (newFilter) => {
    setQuery(newFilter);
  };

  const onCreditTransferClick = (user) => {
    setSelectedCompanyUser(user);
  };

  const handleImpersonate = async (uuid) => {
    companiesApi.impersonate({ uuid }).then((result) => {
      window.open(
        `${process.env.REACT_APP_CUSTOMER_PORTAL_URL}/impersonate/${result.token}`,
        "_blank"
      );
    });
  };

  const onCreditTransferConfirmationClick = () => {
    setOpenCreditTransferConfirmation(true);
  };

  const onCompleteDialogOpen = () => {
    setOpenCompleteDialog(true);
  };

  const onCreditTransferCloseClick = () => {
    setSelectedCompanyUser(null);
  };

  const onCreditTransferConfirmationCloseClick = () => {
    setOpenCreditTransferConfirmation(false);
  };

  const onCompleteDialogClose = () => {
    setOpenCompleteDialog(false);
    onCompanyUsersViewClick({
      companyId: selectedCompanyForUsers.companyId,
      companyPlan: selectedCompanyForUsers.companyPlan,
      page: currentUsersPage,
      length: usersRowsPerPage,
    });
    setTimeout(() => {
      dispatch(clearErrors());
    }, 200);
  };

  const onCreditTransferConfirmSubmitClick = async (values, formik) => {
    setIsSubmitting(true);

    if (values.creditTransferAction === "seqRangeReimbursed") {
      await dispatch(
        postReimbursedCredit({
          company: (selectedCompany && selectedCompany.company && selectedCompany.company.uuid)
            || selectedCompanyForUsers.companyId,
          transaction: {
            rangeType: values.rangeType,
            seqRanges: values.seqRanges,
            snRanges: values.snRanges,
            balance: values.balance,
            remark: values.remark,
          },
          user_info: selectedCompanyUser,
        })
      );
    } else {
      await dispatch(
        postCreditTransfer({
          company: (selectedCompany && selectedCompany.company && selectedCompany.company.uuid)
            || selectedCompanyForUsers.companyId,
          user_info: selectedCompanyUser,
          transaction: values,
        })
      );
    }

    setIsSubmitting(false);
    formik.resetForm();
    onCreditTransferConfirmationCloseClick();
    onCreditTransferCloseClick();
    onCompleteDialogOpen();
  };

  // Expired Management handlers
  const handleSaveExpiredDate = (newDate) => {
    setUpdatingExpired(true);
    companiesApi
      .updateCompanyExpiredDate({
        id: companyData.company.id,
        expiredAt: newDate,
      })
      .then((response) => {
        if (response.success) {
          setCompanyData((prevData) => ({
            ...prevData,
            company: {
              ...prevData.company,
              expired_at: newDate,
              plan: expiredPlan,
            },
          }));
          setEditMode(false);
          setMonthsInput("");
          dispatch(
            addAlert({
              severity: "success",
              message: "Company expiration date updated successfully",
            })
          );
        } else {
          dispatch(
            addAlert({
              severity: "error",
              message:
                response.message || "Failed to update company expiration date",
            })
          );
        }
      })
      .catch((error) => {
        console.error("Error updating company expiration date:", error);
        dispatch(
          addAlert({
            severity: "error",
            message: "Error updating company expiration date",
          })
        );
      })
      .finally(() => {
        setUpdatingExpired(false);
      });
  };

  // Sales Email handlers
  const handleSalesEmailSubmit = async (values, formikHelpers) => {
    setIsSubmittingSalesEmail(true);
    try {
      const id = (selectedCompany.company && selectedCompany.company.id) || selectedCompany.id;
      await updateCompanySalesEmail(id, values.salesEmail);

      dispatch(
        addAlert({
          severity: "success",
          message: "Sales email updated successfully",
        })
      );

      // Update the form with the new value
      formikHelpers.setFieldValue("salesEmail", values.salesEmail);
    } catch (error) {
      console.error("Error updating sales email:", error);
      dispatch(
        addAlert({
          severity: "error",
          message: "Failed to update sales email",
        })
      );
      formikHelpers.setSubmitting(false);
      formikHelpers.setStatus({ error: "Failed to update sales email." });
    } finally {
      setIsSubmittingSalesEmail(false);
    }
  };

  // Company Features handlers
  //
  // Same save path as the standalone company bundle dialog: resolve the bundle the switches
  // describe FIRST (creating it when nothing live carries that module set), then assign it.
  // The create is chained before the assign on purpose — if it is rejected (duplicate module
  // set, name taken) the bundles middleware shows the server's message and nothing is
  // assigned.
  //
  // Neither branch below is silent. A rejected create is alerted by the bundles middleware;
  // a rejected ASSIGN is alerted by companies.mdl's setCompanyBundle middleware, on the same
  // snackbar as the green toasts — so "Bundle Created successfully" can never be the only
  // thing the admin sees after a create that was never assigned to anybody.
  const handleCompanyFeatureSubmit = (values, actions) => {
    setIsUpdatingCompanyFeature(true);
    const companyId = (selectedCompany.company && selectedCompany.company.id)
      || selectedCompany.id;

    // The create was accepted, so the company is being put on the bundle it just made — not
    // on the one the Bundle dropdown was showing when Save was pressed. Move the form onto it
    // BEFORE the assign is attempted, in one setValues so validation never sees half of it.
    // Two things depend on it: the dropdown names the bundle actually about to be written,
    // and a save whose ASSIGN is then rejected can be retried — the retry re-resolves from
    // the form, reads UNCHANGED, and assigns this bundle. Left alone, the form would still
    // name the old bundle while the switches described the new one, which is precisely the
    // ambiguity the resolver refuses, so the admin could never save again.
    // A rejected create never reaches here, so nothing moves and nothing is assigned.
    const adoptCreatedBundle = (created) => {
      if (!actions || !actions.setValues) {
        return;
      }
      actions.setValues((previous) => ({
        ...previous,
        bundle: created.id,
        moduleIds: created.moduleIds,
        newBundleName: "",
      }));
    };

    resolveBundleId({
      dispatch,
      bundleOptions,
      values,
      onBundleCreated: adoptCreatedBundle,
    })
      .then((bundleId) => dispatch(setCompanyBundle({ id: companyId, bundleId })))
      .then((action) => {
        if (!setCompanyBundle.fulfilled.match(action)) {
          setIsUpdatingCompanyFeature(false);
          return;
        }
        dispatch(
          addAlert({
            severity: "success",
            message: "Company features updated successfully",
          })
        );
        // The row behind this dialog still shows the old bundle. Let the list know, so its
        // Bundle column stops contradicting what the admin just saved. Optional, and its own
        // failure is silent by the same reasoning as the confirmation read below.
        if (onCompanyFeatureSaved) {
          onCompanyFeatureSaved();
        }
        // Confirm what the company actually holds now, and only stop the spinner once the
        // answer lands — the panel re-seeds from it. The slice discards this response if the
        // admin has since closed the dialog or moved to another company, so it can never
        // seed somebody else's panel.
        //
        // It is a CONFIRMATION, not the save: the write above already succeeded and the
        // slice has already moved the panel onto the assigned bundle, so a rejection here
        // changes nothing the admin did and must not be reported as a failure. It says so
        // itself — both alert owners stand down for `suppressErrorAlert` — leaving exactly
        // one message for this save, the green one above.
        dispatch(getCompanyFeature({ id: companyId, suppressErrorAlert: true }))
          .finally(() => {
            setIsUpdatingCompanyFeature(false);
          });
      })
      .catch((error) => {
        // A rejected create was alerted by the bundles middleware. The resolver's own refusal
        // — "these modules are a different bundle from the one in the dropdown" — has no
        // middleware behind it, and the panel's validate() blocks it before this runs, so
        // reaching here means the guard was bypassed. Report it rather than stopping the
        // spinner in silence.
        if (isMatchedBundleError(error)) {
          dispatch(addAlert({ severity: "error", message: error.message }));
        }
        setIsUpdatingCompanyFeature(false);
      });
  };

  // The way out of a company-feature read that failed. The panel offers it instead of
  // spinning; this one IS admin-initiated, so its failure is alerted like any other read.
  const handleCompanyFeatureRetry = () => {
    if (!selectedCompany) {
      return;
    }
    const companyId = (selectedCompany.company && selectedCompany.company.id)
      || selectedCompany.id;
    dispatch(getBundleOptions());
    // Full module set again — never the authoring dialogs' active-only subset (see the tab
    // effect above). Refetch unfiltered rather than trusting the shared cache.
    dispatch(getModuleListing());
    dispatch(getCompanyFeature({ id: companyId }));
  };

  // Swap Plan handlers
  const handleSwapPlanSubmit = (values) => {
    setIsUpdatingSwapPlan(true);
    const companyId = (selectedCompany.company && selectedCompany.company.id)
      || selectedCompany.id;

    dispatch(
      updateCompanyPlan({
        id: companyId,
        plan: values.plan ? values.plan : plans.length ? plans[0].uuid : "",
        need_access_right_update: values.need_access_right_update,
      })
    )
      .then(unwrapResult)
      .then(() => {
        dispatch(
          addAlert({
            severity: "success",
            message: "Plan updated successfully",
          })
        );
        // Refetch the updated plan info to refresh the UI
        return dispatch(getCompanyPlanInfo(companyId));
      })
      .catch((error) => {
        dispatch(
          addAlert({
            severity: "error",
            message: "Failed to update plan",
          })
        );
      })
      .finally(() => {
        setIsUpdatingSwapPlan(false);
      });
  };

  // Tag Configuration handlers
  const handleTagSubmit = (values) => {
    setIsUpdatingTags(true);
    const uuid = selectedCompany.company ? selectedCompany.company.id : undefined;

    companiesApi
      .putCompanyTags({uuid, tags: values.tags})
      .then(() => {
        setIsUpdatingTags(false);
        dispatch(addAlert({
          severity: "success",
          message: "Tags updated successfully",
        }));
      })
  }

  return (
    <CompanyDetailsDialogComponent
      industries={industries}
      selectedCompany={selectedCompany}
      onCompanyDetailsDialogCloseClick={handleClose}
      currentTab={currentTab}
      onTabChange={handleTabChange}
      onCompanyWhiteLabelDialogSubmitClick={
        onCompanyWhiteLabelDialogSubmitClick
      }
      onRemoveCompanyWhiteLabelClick={onRemoveCompanyWhiteLabelClick}
      onCompanyUsersViewClick={onCompanyUsersViewClick}
      selectedCompanyForUsers={selectedCompanyForUsers}
      companyBranch={companyBranch}
      companyManageBranchPage={companyManageBranchPage}
      totalCompanyManageBranch={totalCompanyManageBranch}
      selectedBranchWhiteLabel={selectedBranchWhiteLabel}
      rowsPerPage={rowsPerPage}
      currentBranchPage={currentBranchPage}
      confirmationDialogModal={confirmationDialogModal}
      isRemovingBranch={isRemovingBranch}
      isManageBranchLoading={isManageBranchLoading}
      onCompanyManageBranchPageClick={onCompanyManageBranchPageClick}
      handleItemPerPageChange={handleItemPerPageChange}
      onBranchWhiteLabelClick={onBranchWhiteLabelClick}
      onBranchWhiteLabelDialogSubmitClick={onBranchWhiteLabelDialogSubmitClick}
      onBranchWhiteLabelDialogCloseClick={onBranchWhiteLabelDialogCloseClick}
      closeConfirmationDialog={closeConfirmationDialog}
      setConfirmationDialog={setConfirmationDialog}
      onRemoveBranchWhiteLabelClick={onRemoveBranchWhiteLabelClick}
      companyUsers={companyUsers}
      totalUsers={totalUsers}
      currentUsersPage={currentUsersPage}
      isCompanyUsersLoading={isCompanyUsersLoading}
      completeDialogErrors={completeDialogErrors}
      usersRowsPerPage={usersRowsPerPage}
      query={query}
      selectedCompanyUser={selectedCompanyUser}
      openCreditTransferConfirmation={openCreditTransferConfirmation}
      isSubmitting={isSubmitting}
      openCompleteDialog={openCompleteDialog}
      onCompanyUsersPageClick={onCompanyUsersPageClick}
      onClearFilter={onClearFilter}
      handleUsersItemPerPageChange={handleUsersItemPerPageChange}
      onSearchSubmit={onSearchSubmit}
      onCreditTransferClick={onCreditTransferClick}
      onCreditTransferConfirmationClick={onCreditTransferConfirmationClick}
      onCompleteDialogOpen={onCompleteDialogOpen}
      onCreditTransferCloseClick={onCreditTransferCloseClick}
      onCreditTransferConfirmationCloseClick={
        onCreditTransferConfirmationCloseClick
      }
      onCompleteDialogClose={onCompleteDialogClose}
      onCreditTransferConfirmSubmitClick={onCreditTransferConfirmSubmitClick}
      handleImpersonate={handleImpersonate}
      canImpersonate={canImpersonate}
      canTransferCredit={canTransferCredit}
      // Expired Management props
      editMode={editMode}
      setEditMode={setEditMode}
      monthsInput={monthsInput}
      setMonthsInput={setMonthsInput}
      companyData={companyData}
      loadingExpired={loadingExpired}
      updatingExpired={updatingExpired}
      handleSaveExpiredDate={handleSaveExpiredDate}
      // Sales Email props
      isSubmittingSalesEmail={isSubmittingSalesEmail}
      handleSalesEmailSubmit={handleSalesEmailSubmit}
      // Company Features props
      bundleOptions={bundleOptions}
      isCompanyFeatureLoaded={isCompanyFeatureLoaded}
      hasCompanyFeatureLoadFailed={hasCompanyFeatureLoadFailed}
      moduleListing={moduleListing}
      featureStructure={featureStructure}
      companyFeature={companyFeature}
      companyFeatureError={companyFeatureError}
      isCompanyFeatureLoading={isCompanyFeatureLoading}
      isUpdatingCompanyFeature={isUpdatingCompanyFeature}
      handleCompanyFeatureSubmit={handleCompanyFeatureSubmit}
      handleCompanyFeatureRetry={handleCompanyFeatureRetry}
      // Swap Plan props
      planInfo={planInfo}
      plans={plans}
      isLoadingSwapPlan={isLoadingSwapPlan}
      isUpdatingSwapPlan={isUpdatingSwapPlan}
      handleSwapPlanSubmit={handleSwapPlanSubmit}
      // Tag Configuration props
      companyTags={companyTags}
      isLoadingTags={isLoadingTags}
      isUpdatingTags={isUpdatingTags}
      handleTagSubmit={handleTagSubmit}
    />
  );
}
